import React from "react";
import { Lock, CreditCard, ExternalLink } from "lucide-react";

/**
 * Locked screen shown when the athlete's coaching payment isn't active.
 * Blocks all program content behind a burgundy-accent "Complete payment"
 * card that links to the athlete-specific Stripe payment link (or the
 * studio default when no override is set).
 *
 * Props:
 *   paymentLink: string    // resolved URL (may be empty — link then hidden)
 *   programTitle?: string  // optional, shown in the header for context
 */
export default function LockedProgramCard({ paymentLink, programTitle }) {
  const link = (paymentLink || "").trim();
  return (
    <div
      className="bg-white rounded-lg border border-[var(--tir-border)] border-l-[6px] border-l-[var(--tir-burgundy)] p-8 sm:p-10 shadow-sm"
      data-testid="locked-program-card"
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 h-14 w-14 rounded-full bg-[#fbeae9] flex items-center justify-center">
          <Lock size={26} className="text-[var(--tir-burgundy)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-burgundy)] mb-1">
            Program locked
          </div>
          <h3 className="font-serif-display text-3xl sm:text-4xl text-[var(--tir-ink)] leading-tight tracking-wide">
            Complete your payment to unlock your program
          </h3>
          {programTitle && (
            <div className="text-sm text-[var(--tir-muted)] mt-2 font-mono-tir">
              {programTitle}
            </div>
          )}
          <p className="text-sm text-[var(--tir-muted)] mt-4 leading-relaxed">
            Once your coaching payment is complete, Natalie will manually unlock
            your program (usually within a few hours) and your full weekly
            training will appear here.
          </p>
          {link ? (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="locked-program-pay-btn"
              className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-[var(--tir-burgundy)] text-white text-xs tracking-[0.22em] uppercase font-semibold hover:bg-[#5e1521] transition-colors"
            >
              <CreditCard size={14} /> Complete payment
              <ExternalLink size={12} />
            </a>
          ) : (
            <div
              className="mt-6 text-xs text-[var(--tir-muted)] italic"
              data-testid="locked-program-no-link"
            >
              Reach out to your coach to receive your payment link.
            </div>
          )}
          <div className="text-[10px] tracking-[0.22em] uppercase font-mono-tir text-[var(--tir-muted)] mt-6">
            Already paid via e-transfer or subscription? Your coach will mark you active shortly.
          </div>
        </div>
      </div>
    </div>
  );
}
